import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types";

/**
 * Catálogo "clave producto/servicio SAT → cuenta contable" (equivalente de
 * dbo.Tag) · deriva la cuenta contable del prorrateo FB60 a partir de la
 * clave SAT del concepto del CFDI. Paginado server-side (P42).
 */
export interface MapeoClaveCuenta {
  id: string;
  claveSat: string;
  claveSatDescripcion: string | null;
  cuentaContableId: string;
  cuentaCodigo: string;
  cuentaNombre: string;
  nombre: string;
  activo: boolean;
}

export interface CrearMapeoClaveCuentaInput {
  claveSat: string;
  cuentaContableId: string;
  nombre: string;
}

export type EditarMapeoClaveCuentaInput = CrearMapeoClaveCuentaInput;

export interface ListarMapeosParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Fila cruda del Excel de importación · clave SAT + CÓDIGO de la cuenta (no id). */
export interface FilaImportacionMapeo {
  claveSat: string;
  cuentaCodigo: string;
  nombre: string;
}

/** Detalle por fila del preview de importación · qué se hará con cada renglón. */
export interface PreviewFilaMapeo {
  renglon: number;
  claveSat: string;
  cuentaCodigo: string;
  nombre: string | null;
  estado: "Crear" | "Actualizar" | "Omitir";
  motivo: string | null;
}

/** Resumen devuelto por el endpoint de importación masiva (real y dry-run). */
export interface ResultadoImportacionMapeo {
  creados: number;
  actualizados: number;
  omitidos: number;
  errores: string[];
  detalle: PreviewFilaMapeo[];
}

/** Listado PAGINADO server-side · filtro `busqueda` (contains normalizado). */
export async function listarMapeosClaveCuenta(
  params: ListarMapeosParams = {}
): Promise<Paginado<MapeoClaveCuenta>> {
  const { data } = await axiosClient.get<Paginado<MapeoClaveCuenta>>(
    "/api/admin/mapeos-clave-cuenta",
    { params }
  );
  return data;
}

export async function crearMapeoClaveCuenta(
  input: CrearMapeoClaveCuentaInput
): Promise<{ id: string }> {
  const { data } = await axiosClient.post<{ id: string }>(
    "/api/admin/mapeos-clave-cuenta",
    input
  );
  return data;
}

export async function editarMapeoClaveCuenta(
  id: string,
  input: EditarMapeoClaveCuentaInput
): Promise<void> {
  await axiosClient.patch(`/api/admin/mapeos-clave-cuenta/${id}`, input);
}

/** Baja lógica / reactivación · NUNCA hard delete (la cuenta deriva prorrateos históricos). */
export async function cambiarEstadoMapeoClaveCuenta(
  id: string,
  activo: boolean
): Promise<void> {
  await axiosClient.post(`/api/admin/mapeos-clave-cuenta/${id}/estado`, { activo });
}

/**
 * Importa (o valida en dry-run) las filas. Con `soloValidar=true` el backend
 * corre la misma lógica SIN persistir y devuelve el detalle por fila para el
 * preview; con `false` persiste de verdad.
 */
export async function importarMapeosClaveCuenta(
  filas: FilaImportacionMapeo[],
  soloValidar = false
): Promise<ResultadoImportacionMapeo> {
  const { data } = await axiosClient.post<ResultadoImportacionMapeo>(
    "/api/admin/mapeos-clave-cuenta/importar",
    { filas, soloValidar }
  );
  return data;
}

/** Resumen del borrado masivo · elimina si nada la usa, desactiva si algo la usa. */
export interface ResultadoEliminarMasivoMapeo {
  eliminados: number;
  desactivados: number;
  noEncontrados: number;
}

/**
 * Eliminación masiva: por cada mapeo seleccionado, borra permanentemente si NADA
 * lo usa en un prorrateo, o lo desactiva (baja lógica) si algún prorrateo derivó
 * su cuenta de él.
 */
export async function eliminarMasivoMapeosClaveCuenta(
  ids: string[]
): Promise<ResultadoEliminarMasivoMapeo> {
  const { data } = await axiosClient.post<ResultadoEliminarMasivoMapeo>(
    "/api/admin/mapeos-clave-cuenta/eliminar-masivo",
    { ids }
  );
  return data;
}
